import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  computeCompositeRisk,
  listRisks,
  setComplianceContext,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type { CompositeRiskResponse } from "../../types/compliance";
import VATValidationPanel from "./VATValidationPanel";
import InvoiceValidationPanel from "./InvoiceValidationPanel";
import ShipmentValidationPanel from "./ShipmentValidationPanel";
import ShipmentCasePicker from "./ShipmentCasePicker";
import WorkflowV2Insights from "./WorkflowV2Insights";

type WorkflowRow = {
  workflowId: string;
  riskCount: number;
  highRiskCount: number;
  composite?: CompositeRiskResponse;
};

export default function WorkflowCenter() {
  const { credentials } = useComplianceAuth();
  const tenantId = credentials?.tenantId ?? "default";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<WorkflowRow[]>([]);
  const [expandedWorkflow, setExpandedWorkflow] = useState<string | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [caseWorkflowIds, setCaseWorkflowIds] = useState<string[]>([]);

  useEffect(() => {
    setComplianceContext(tenantId, credentials?.role ?? "COMPLIANCE_ADMIN");
    void load();
  }, [tenantId, credentials?.role, selectedCaseId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const riskResp = await listRisks(undefined, selectedCaseId || undefined);
      const grouped = new Map<string, { total: number; high: number }>();

      for (const risk of riskResp.items) {
        const curr = grouped.get(risk.workflow_id) ?? { total: 0, high: 0 };
        curr.total += 1;
        if (risk.severity === "high") curr.high += 1;
        grouped.set(risk.workflow_id, curr);
      }

      const workflowRows: WorkflowRow[] = [];
      for (const [workflowId, counts] of grouped.entries()) {
        let composite: CompositeRiskResponse | undefined;
        try {
          composite = await computeCompositeRisk(workflowId);
        } catch {
          // Keep row even if no composite can be computed yet.
        }
        workflowRows.push({
          workflowId,
          riskCount: counts.total,
          highRiskCount: counts.high,
          composite,
        });
      }

      workflowRows.sort((a, b) => {
        const as = a.composite?.overall_risk_score ?? 0;
        const bs = b.composite?.overall_risk_score ?? 0;
        return bs - as;
      });

      setRows(workflowRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load workflows");
    } finally {
      setLoading(false);
    }
  }

  const displayedRows = selectedCaseId
    ? caseWorkflowIds.map(
        (workflowId) =>
          rows.find((row) => row.workflowId === workflowId) ?? {
            workflowId,
            riskCount: 0,
            highRiskCount: 0,
          },
      )
    : rows;

  const totalHigh = useMemo(
    () => displayedRows.reduce((sum, row) => sum + row.highRiskCount, 0),
    [displayedRows],
  );

  const topScore = displayedRows.reduce(
    (max, row) => Math.max(max, row.composite?.overall_risk_score ?? 0),
    0,
  );

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Activity size={24} className="text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">Workflow Center</h1>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <p className="text-xs text-gray-500 uppercase font-medium">
            {selectedCaseId ? "Case Workflows" : "Workflows"}
          </p>
          <p className="text-2xl font-bold text-gray-900">{displayedRows.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <p className="text-xs text-gray-500 uppercase font-medium">
            High Risks
          </p>
          <p className="text-2xl font-bold text-red-700">{totalHigh}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <p className="text-xs text-gray-500 uppercase font-medium">
            Top Risk Score
          </p>
          <p className="text-2xl font-bold text-amber-700">
            {topScore
              ? `${Math.round(topScore * 100)}%`
              : "0%"}
          </p>
        </div>
      </div>

      <div className="mb-6">
        <ShipmentCasePicker
          selectedCaseId={selectedCaseId}
          selectedWorkflowId={expandedWorkflow ?? undefined}
          onSelectCase={(shipmentCase, detail) => {
            const workflowIds = detail.documents.map((doc) => doc.workflow_id);
            setSelectedCaseId(shipmentCase.case_id);
            setCaseWorkflowIds(workflowIds);
            setExpandedWorkflow(workflowIds[0] ?? null);
          }}
          onSelectWorkflow={(document) => setExpandedWorkflow(document.workflow_id)}
        />
        {selectedCaseId && (
          <p className="mt-2 text-xs text-gray-500">
            Active shipment case workflow set: <span className="font-mono">{selectedCaseId}</span>. Select invoice, eCMR, packing list, delivery note, or customs declaration to run validation against that document workflow.
          </p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">
            Workflows ({rows.length}) — click to validate
          </h2>
        </div>
        {loading ? (
          <div className="p-10 text-center text-gray-500">
            <Loader2 className="inline-block animate-spin mr-2" size={18} />
            Loading workflows...
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-red-700 bg-red-50 border-t border-red-100">
            {error}
          </div>
        ) : displayedRows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">
            {selectedCaseId ? "No document workflows are saved for this case." : "No workflow risks yet. Start by uploading documents."}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {displayedRows.map((row) => {
              const isExpanded = expandedWorkflow === row.workflowId;
              return (
                <div key={row.workflowId}>
                  {/* Header row — clickable to expand */}
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedWorkflow(isExpanded ? null : row.workflowId)
                    }
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isExpanded ? (
                        <ChevronDown
                          size={14}
                          className="text-gray-400 shrink-0"
                        />
                      ) : (
                        <ChevronRight
                          size={14}
                          className="text-gray-400 shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <p className="font-mono text-sm text-gray-900 truncate">
                          {row.workflowId}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {row.riskCount} risk events · {row.highRiskCount} high
                          severity
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-sm font-semibold text-gray-900">
                        {row.composite
                          ? `${Math.round(row.composite.overall_risk_score * 100)}%`
                          : "—"}
                      </p>
                      <p className="text-[10px] uppercase text-gray-500">
                        {row.composite?.severity ?? "unknown"}
                      </p>
                    </div>
                  </button>

                  {/* Expanded validation panels */}
                  {isExpanded && (
                    <div className="border-t border-gray-50 px-4 pb-4 pt-3">
                      <div className="mb-3">
                        <WorkflowV2Insights workflowId={row.workflowId} />
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <VATValidationPanel
                          workflowId={row.workflowId}
                          tenantId={tenantId}
                          onComplete={() => void load()}
                        />
                        <InvoiceValidationPanel
                          workflowId={row.workflowId}
                          tenantId={tenantId}
                          onComplete={() => void load()}
                        />
                        <ShipmentValidationPanel
                          workflowId={row.workflowId}
                          tenantId={tenantId}
                          onComplete={() => void load()}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
